import React from 'react';
import { useNavigate } from 'react-router-dom';
import { color, font, tint } from '../tokens';
import { contractEnabled } from '../data/contractFlag';
import { useSelfManaged } from '../hooks/useAuthSession';
import { useAssessorTab } from '../hooks/assessorTab';

/** Assessments (spec 5): Phil and assessor staff only - see `assessments` below. */
const ASSESSMENTS_TAB = { key: 'assessments', label: 'Assessments', icon: 'chart', route: '/portal/assessments' };

/**
 * Bottom tab bar. Four items per role, carried forward from the 2025 build's
 * dashboard tab strips — on a phone those become bottom nav, trimmed to four.
 *
 * Navigation is built in: each tab carries its route and the bar navigates by
 * default, so a screen only renders `<BottomTabBar role active>` and gets a
 * working nav. `onChange` overrides that (the harness passes a no-op so its
 * gallery swatch doesn't navigate). Fixing this here rather than threading
 * onChange through every screen is deliberate — the first build passed neither,
 * and eleven screens shipped with an inert primary nav.
 *
 * Icons are 20px geometric placeholders, as in the scaffold. The real icon set
 * still has to be supplied; the name on each tab is the icon meaning.
 */
export const TABS = {
  athlete: [
    { key: 'home', label: 'Home', icon: 'home', route: '/portal/home' },
    { key: 'schedule', label: 'Schedule', icon: 'calendar', route: '/portal/schedule' },
    { key: 'contract', label: 'Contract', icon: 'target', route: '/portal/contract' },
    // DNA's old slot (off for players since 2026-09-01) is now Tour (Sprint 7
    // pin, TEAM.md): the RYP Tour leaderboard, /portal/tour.
    { key: 'tour', label: 'Tour', icon: 'trophy', route: '/portal/tour' },
  ],
  /**
   * The self-managed athlete (Mike S6 2026-09-30): the 18+ member who signed
   * up for themselves pays for themselves, so the parent's Billing and
   * Settings join the athlete set, in the parent bar's order. Selected by
   * `selfManaged` below, never for a child's login.
   */
  athleteSelfManaged: [
    { key: 'home', label: 'Home', icon: 'home', route: '/portal/home' },
    { key: 'schedule', label: 'Schedule', icon: 'calendar', route: '/portal/schedule' },
    { key: 'contract', label: 'Contract', icon: 'target', route: '/portal/contract' },
    { key: 'billing', label: 'Billing', icon: 'card', route: '/portal/billing' },
    { key: 'tour', label: 'Tour', icon: 'trophy', route: '/portal/tour' },
    { key: 'settings', label: 'Settings', icon: 'settings', route: '/portal/settings' },
  ],
  parent: [
    // No separate Children tab: Home IS the children overview, and the old
    // /portal/athlete route it pointed at now redirects to /portal/family —
    // two tabs, one destination (owner's report, 2026-09-01).
    { key: 'home', label: 'Home', icon: 'home', route: '/portal/family' },
    // Sprint 11 pin F (TEAM.md, contract v1.9): Reservations replaces
    // Billing's old tab slot (Billing itself was already removed, Sprint 7 —
    // this is the second tab-bar swap in that slot, not a Billing return).
    // Uses the 'calendar' icon already in the icon-set list (BottomTabBar's
    // athlete 'schedule' tab uses the same glyph meaning) rather than adding
    // a new icon name nobody has drawn yet.
    { key: 'reservations', label: 'Reservations', icon: 'calendar', route: '/portal/reservations' },
    // Sprint 16 (contract v2.4): Billing returns as the parents' token hub.
    { key: 'billing', label: 'Billing', icon: 'card', route: '/portal/billing' },
    { key: 'tour', label: 'Tour', icon: 'trophy', route: '/portal/tour' },
    { key: 'settings', label: 'Settings', icon: 'settings', route: '/portal/settings' },
  ],
  // Sprint 5 pin (TEAM.md): the Me tab is removed - the coach profile screen
  // was never built and the slot had no route to send anyone to.
  coach: [
    { key: 'today', label: 'Today', icon: 'today', route: '/portal/coach' },
    { key: 'roster', label: 'Roster', icon: 'list', route: '/portal/roster' },
    ASSESSMENTS_TAB,
  ],
  /**
   * Sprint 10 pin F (TEAM.md, "STAFF NAVIGATION"): a coach WITH specialistId
   * (Phil) - Sessions / Roster / Capture, the plain coach set with Today
   * swapped for the specialist's own My Sessions day view. Selected below via
   * the `specialistId` prop rather than a `role="coachSpecialist"` string, so
   * every existing `<BottomTabBar role="coach" .../>` call site is untouched.
   */
  coachSpecialist: [
    // Sessions stays first: it is where Phil lands (landingFor in PortalRoutes).
    { key: 'sessions', label: 'Sessions', icon: 'today', route: '/portal/my-sessions' },
    // Owner ruling 2026-11-03: Phil sees the day's golf blocks and who is
    // booked in each (the coach's Today, /portal/coach) beside his own
    // Sessions. The rules already let a coach read every roster and mark
    // golf attendance.
    { key: 'today', label: 'Today', icon: 'calendar', route: '/portal/coach' },
    // No Roster tab for Phil (PM integration): rosters are assignment-based
    // and a specialist coach is nobody's assigned golf coach, so that tab
    // would always be empty for him. Tour replaces it.
    ASSESSMENTS_TAB,
    { key: 'tour', label: 'Tour', icon: 'trophy', route: '/portal/tour' },
  ],
  /**
   * Sprint 10 pin F: the three staff role tab sets, all pointing at the same
   * three staff-surface routes in a per-role order/subset - Admin
   * (AdminDashboard), Sessions (SpecialistDay/My Sessions - ops/owner get the
   * specialist switcher there per that screen's `canSwitch`), Staff (owner
   * only, StaffRoles), and Tour (every role already gets this one).
   */
  // Owner ruling 2026-11-02: owners and ops see the day's golf blocks and who
  // is booked in each (the coach's Today), beside the specialists' Sessions.
  owner: [
    { key: 'admin', label: 'Admin', icon: 'admin', route: '/portal/admin' },
    { key: 'today', label: 'Today', icon: 'calendar', route: '/portal/coach' },
    { key: 'sessions', label: 'Sessions', icon: 'today', route: '/portal/my-sessions' },
    { key: 'staff', label: 'Staff', icon: 'people', route: '/portal/staff' },
    { key: 'tour', label: 'Tour', icon: 'trophy', route: '/portal/tour' },
  ],
  ops: [
    { key: 'admin', label: 'Admin', icon: 'admin', route: '/portal/admin' },
    { key: 'today', label: 'Today', icon: 'calendar', route: '/portal/coach' },
    { key: 'sessions', label: 'Sessions', icon: 'today', route: '/portal/my-sessions' },
    { key: 'tour', label: 'Tour', icon: 'trophy', route: '/portal/tour' },
  ],
  mental: [
    // Sessions stays first: it is where Yannick lands (landingFor in PortalRoutes).
    { key: 'sessions', label: 'Sessions', icon: 'today', route: '/portal/my-sessions' },
    // Owner ruling 2026-11-03: Yannick sees the day's golf blocks and who is
    // booked in each too. A golf roster opens read-only for him: the rules
    // let a mental account mark attendance on its own sessions only.
    { key: 'today', label: 'Today', icon: 'calendar', route: '/portal/coach' },
    // 2026-10-28 (spec 5): Yannick's athletes and his contract board. The
    // board's key is NOT 'contract' - that key is hidden while the family
    // switch is off, and the board is not behind it.
    { key: 'athletes', label: 'Athletes', icon: 'people', route: '/portal/mental' },
    { key: 'mental-contract', label: 'Contract', icon: 'target', route: '/portal/mental/contract' },
    // Assessments only when the owner gave this mental account the permission.
    ASSESSMENTS_TAB,
    { key: 'tour', label: 'Tour', icon: 'trophy', route: '/portal/tour' },
  ],
};

/**
 * @param {string} [specialistId]  Sprint 10 pin F: when `role === 'coach'`
 *   and this is set (Phil's own account, users.specialistId), the Phil tab
 *   set (Sessions / Roster / Capture) renders instead of the plain coach one.
 *   Ignored for every other role.
 * @param {boolean} [selfManaged]  With `role === 'athlete'`: the self-managed
 *   set (Billing, Settings). Defaults to the signed-in session's
 *   user.selfManaged (useSelfManaged), so no athlete screen threads it.
 * @param {boolean} [assessments]  The Assessments tab on the coach, Phil and
 *   mental sets. Defaults to the signed-in session's flag (useAssessorTab:
 *   Phil, or the owner's Assessments permission); Phil's specialistId always
 *   shows it. No other role's set has the tab.
 */
export default function BottomTabBar({ role = 'athlete', active, onChange, specialistId, selfManaged, assessments }) {
  const sessionSelfManaged = useSelfManaged();
  const sessionAssessor = useAssessorTab();
  const payer = role === 'athlete' && (selfManaged ?? sessionSelfManaged) === true;
  const showAssessments = (assessments ?? sessionAssessor) === true || specialistId === 'phil';
  const set = role === 'coach' && specialistId ? TABS.coachSpecialist : payer ? TABS.athleteSelfManaged : TABS[role] || TABS.athlete;
  // The Contract tab goes while the contract is hidden (owner, 2026-09-30).
  const items = set.filter((tab) => (contractEnabled() || tab.key !== 'contract') && (tab.key !== 'assessments' || showAssessments));
  const navigate = useNavigate();

  const select = (tab) => {
    if (onChange) return onChange(tab.key);
    if (tab.route) return navigate(tab.route);
    return undefined; // tab exists in the IA but its screen is not built yet
  };

  return (
    <nav
      style={{
        borderTop: `1px solid ${color.frameRule}`,
        padding: '9px 8px 20px',
        background: color.bg,
        display: 'flex',
      }}
    >
      {items.map((tab) => {
        const on = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            aria-current={on ? 'page' : undefined}
            onClick={() => select(tab)}
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              padding: '6px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              data-icon={tab.icon}
              style={{
                width: 20,
                height: 20,
                borderRadius: 5,
                background: on ? tint.greenStrong : 'transparent',
                border: `1.5px solid ${on ? color.primary : color.mutedText}`,
              }}
            />
            <span
              style={{
                font: `500 10px ${font.body}`,
                color: on ? color.primary : color.textTertiary,
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
